import React from "react";
import T from '../../components/common/T';
import { useNavigate } from "react-router-dom";
import { useProjects } from "../../context/ProjectContext";
import { useTasks } from "../../context/TaskContext"; 
import { usePurchaseOrder } from "../../context/PurchaseOrderContext"; 
import Price from "../../components/common/Price";
import KanbanBoard from "../../components/common/KanbanBoard";
import { formatDate } from "../../utils/dateUtils";

const MoreVerticalIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01" />
  </svg>
);

const ProjectKanbanView = () => {
  const { projects, updateProject } = useProjects();
  const { tasks } = useTasks();
  const { purchaseOrders } = usePurchaseOrder();
  const navigate = useNavigate();

  // 1. Define Columns matching the database status fields
  const COLUMNS = [
    { id: "Pending", title: "Pending", description: "Not started yet", dotColor: "bg-amber-500" },
    { id: "In Progress", title: "In Progress", description: "Actively working", dotColor: "bg-blue-500" },
    { id: "Completed", title: "Completed", description: "Finished projects", dotColor: "bg-emerald-500" }
  ];

  // 2. Handle Drop
  const handleDragEnd = (result) => {
    const { destination, draggableId } = result;
    if (!destination) return;
    
    // Update backend with new status (destination.droppableId corresponds to the column id)
    updateProject(draggableId, { status: destination.droppableId });
  };

  // 3. Define Card UI
  const renderProjectCard = (project) => {
    // Local Calculations for the Card
    const pTasks = tasks.filter(t => String(t.projectId) === String(project.id));
    const doneTasks = pTasks.filter(t => t.status === "Completed").length;
    const progress = pTasks.length > 0 ? Math.round((doneTasks / pTasks.length) * 100) : 0;
    
    const spent = purchaseOrders
      .filter(po => String(po.projectId) === String(project.id) && po.status === "Completed")
      .reduce((sum, po) => sum + Number(po.totalPrice), 0);

    return (
      <div className="bg-white dark:bg-[#161b22] p-4 rounded-xl shadow-xs border border-gray-200 dark:border-[#30363d] hover:shadow-md transition group cursor-grab active:cursor-grabbing">
        <div className="flex justify-between items-start mb-2">
          <h4 className="font-bold text-gray-800 dark:text-[#f0f6fc] text-base group-hover:text-[#D9531E] dark:group-hover:text-[#F5875E] transition leading-tight">
            {project.name}
          </h4>
          <button 
            onClick={(e) => { e.stopPropagation(); navigate(`/admin/project/${project.id}`); }} 
            className="text-gray-400 dark:text-[#6e7681] hover:text-gray-700 dark:hover:text-white p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-[#21262d] transition"
          >
            <MoreVerticalIcon />
          </button>
        </div>
        
        <div className="flex justify-between items-center text-xs text-gray-500 dark:text-[#848d97] mb-3">
          <span>{project.client}</span>
          {project.endDate && <span className="text-[10px] bg-gray-100 dark:bg-[#21262d] px-1.5 py-0.5 rounded font-mono">{formatDate(project.endDate)}</span>}
        </div>
        
        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-[10px] mb-1">
            <span className="font-semibold text-gray-500 dark:text-[#848d97]"><T>Tasks:</T> {doneTasks}/{pTasks.length}</span>
            <span className="font-bold text-[#D9531E] dark:text-[#F5875E]">{progress}%</span>
          </div>
          <div className="w-full bg-gray-100 dark:bg-[#21262d] rounded-full h-1.5 overflow-hidden">
            <div 
              className={`h-1.5 rounded-full transition-all duration-500 ${progress === 100 ? 'bg-emerald-500' : 'bg-[#D9531E]'}`} 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Financials */}
        <div className="grid grid-cols-2 gap-2 pt-3 border-t border-gray-100 dark:border-[#30363d]">
          <div>
            <span className="block text-[10px] text-gray-400 dark:text-[#848d97] font-bold uppercase"><T>Budget</T></span>
            <span className="text-xs font-bold text-gray-700 dark:text-[#f0f6fc]"><Price amount={project.budget} /></span>
          </div>
          <div>
            <span className="block text-[10px] text-gray-400 dark:text-[#848d97] font-bold uppercase"><T>Spent</T></span>
            <span className={`text-xs font-bold ${spent > project.budget ? 'text-red-500' : 'text-gray-700 dark:text-[#f0f6fc]'}`}>
              <Price amount={spent} />
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <KanbanBoard 
      columns={COLUMNS}
      items={projects}
      groupBy="status"
      onDragEnd={handleDragEnd}
      renderCard={renderProjectCard}
    />
  );
};

export default ProjectKanbanView;